import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { Invitation, SiteWithPermissions } from '@sine/sine-core-api-types';
import {
  Form,
  Grid,
  type ISelectOption,
  Input,
  Loader,
  SingleSelect,
  TextArea,
  useJoiForm,
} from '@sine/sine-ui-components';

import formatSingularPlural from '../../../../helpers/formatSingularPlural';
import { storage } from '../../../../services/storage/storage.service';
import { useSiteRequirementsQuery } from '../../../../shared/hooks/useSiteRequirementsQuery';
import type { InviteesRef } from '../../Invitations.types';
import { type InvitationFormRef } from '../../Invitations.types';
import { useCreateInvitationRequirements } from '../../hooks/useCreateInvitationRequirements';
import { useCreateInvitations } from '../../hooks/useCreateInvitations';
import { useInviteeManagement } from '../../hooks/useInviteeManagement';
import { useSiteAndVisitorTypeSelection } from '../../hooks/useSiteAndVisitorTypeSelection';
import { useUpdateInvitation } from '../../hooks/useUpdateInvitation';
import { useZoneOptions } from '../../hooks/useZoneOptions';
import { HIDE_CONFIRM_INVITATION_STORE_KEY } from '../../invitations.constants';
import { ConfirmInvitationModal } from './ConfirmInvitationModal';
import { EmailNotificationControl } from './EmailNotificationControl';
import { InvitationDurationSelector } from './InvitationDurationSelector';
import { InviteeList } from './InviteeList';

interface CreateInvitationFormProps {
  invitation: Invitation | undefined;
  allSites: Array<SiteWithPermissions>;
  siteOptions: Array<{ label: string; value: string; countryCode?: string }>;
  inviteesRef: InviteesRef;
  onClose: VoidFunction;
}

export interface DurationValues {
  expiry?: Invitation['expiry'];
  startDate?: Invitation['startDate'];
  endDate?: Invitation['endDate'];
}

const MAX_VISITOR_MESSAGE_CHAR_COUNT = 4000;
const MAX_ADMIN_MESSAGE_CHAR_COUNT = 1000;

export const CreateInvitationForm = forwardRef<InvitationFormRef, CreateInvitationFormProps>(
  ({ invitation, allSites, siteOptions, inviteesRef, onClose }, ref) => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const hasInitialisedInvitationHost = useRef(false);

    const { selectedSiteId, setSelectedSiteId, selectedVisitorTypeId, setSelectedVisitorTypeId } =
      useSiteAndVisitorTypeSelection(invitation, allSites);

    const { siteRequirements, isLoadingSiteRequirements } = useSiteRequirementsQuery('sinein', selectedSiteId);

    const {
      inviteeIds,
      emailInvitations,
      canSendEmail,
      addInvitee,
      removeInvitee,
      addEmail,
      removeEmail,
      toggleSendEmail,
    } = useInviteeManagement();

    const { schema, initialValues, visitorTypeOptions, hostOptions, requireHostSelection, requireZoneSelection } =
      useCreateInvitationRequirements({
        invitation,
        selectedSite: allSites.find((s) => s.id === selectedSiteId)!,
        selectedVisitorTypeId,
        siteRequirements,
        isLoading: isLoadingSiteRequirements,
      });

    const computedInitialValues = useMemo(
      () => ({
        ...initialValues.invitationDetails,
        siteOption: siteOptions.filter((option) => option.value === selectedSiteId)[0],
        visitorTypeOption: invitation
          ? visitorTypeOptions.filter((option) => option.value === invitation.visitorType)[0]
          : visitorTypeOptions[0],
        hostOption: invitation ? hostOptions.filter((option) => option.value === invitation.host)[0] : hostOptions[0],
      }),
      [],
    );

    const form = useJoiForm(schema, computedInitialValues);

    // Note: if we apply this logic as an `onChange` handler on the select itself; we lose sync between states
    useEffect(() => {
      setSelectedVisitorTypeId(form.values.visitorTypeOption?.value as string);
    }, [form.values.visitorTypeOption?.value]);

    useEffect(() => {
      form.onChange('siteOption', siteOptions.find((option) => option.value === selectedSiteId)!);
    }, [selectedSiteId]);

    const { zoneOptions, defaultZone } = useZoneOptions(
      selectedSiteId,
      form.values?.hostOption?.value,
      invitation?.zoneId || '',
    );

    const { createInvitations } = useCreateInvitations({ onClose });
    const { updateInvitation } = useUpdateInvitation({ onClose });

    function validateAllForms() {
      const inviteesValid = Object.values(inviteesRef.current?.getForms() || {})
        .map((_form) => _form.validate())
        .every((isValid) => isValid);

      return !!(inviteesValid && form.validate());
    }

    useImperativeHandle(ref, () => ({
      handleCreateInvitations: async () => {
        if (validateAllForms()) {
          const shouldShowConfirmation =
            canSendEmail &&
            inviteeIds.some((id) => !emailInvitations.has(id)) &&
            !JSON.parse(storage.getItem(HIDE_CONFIRM_INVITATION_STORE_KEY) || 'false');

          if (shouldShowConfirmation) {
            setShowConfirmationModal(true);
          } else {
            await createInvitations({ inviteesFormRef: inviteesRef, invitationDetails: form.values, canSendEmail });
          }
        }
      },
      handleUpdateInvitation: async (existingInvitation: Invitation) => {
        if (validateAllForms()) {
          await updateInvitation({
            existingInvitation,
            inviteesFormRef: inviteesRef,
            invitationDetails: form.values,
            canSendEmail,
          });
        }
      },
      hasUnsavedChanges: () => {
        const inviteeForms = inviteesRef.current?.getForms() || {};
        const hasInviteeData = Object.values(inviteeForms).some((inviteeForm) => {
          const values = inviteeForm.values;
          return !!(values.firstName || values.lastName || values.email || values.mobile);
        });

        const hasMainFormData = !!(
          form.values.company ||
          form.values.subject ||
          form.values.adminNotes ||
          form.values.visitorNotes
        );

        return hasInviteeData || hasMainFormData;
      },
    }));

    const hasNoFormSelections = useMemo(
      () => !form.values.visitorTypeOption && !form.values.hostOption,
      [form.values.hostOption, form.values.visitorTypeOption],
    );

    /**
     * This effect is messy - but necessary given the dependant nature of the dropdown fields in this form.
     */
    useEffect(() => {
      if (isLoadingSiteRequirements) return;

      if (hasNoFormSelections) {
        if (invitation) {
          const visitorTypeOption = visitorTypeOptions.filter((option) => option.value === invitation.visitorType)[0];
          const hostOption = requireHostSelection
            ? hostOptions.find((option) => option.value === invitation.host)
            : hostOptions[0];

          form.onChange('visitorTypeOption', visitorTypeOption);
          form.onChange('hostOption', hostOption);
        } else {
          const visitorTypeOption = selectedVisitorTypeId
            ? visitorTypeOptions.filter((option) => option.value === selectedVisitorTypeId)[0] || visitorTypeOptions[0]
            : visitorTypeOptions[0];

          const hostOption = requireHostSelection ? hostOptions[0] : undefined;

          form.onChange('visitorTypeOption', visitorTypeOption);
          form.onChange('hostOption', hostOption);
        }
      } else {
        const visitorTypeOption = selectedVisitorTypeId
          ? visitorTypeOptions.filter((option) => option.value === selectedVisitorTypeId)[0] || visitorTypeOptions[0]
          : visitorTypeOptions[0];

        let hostOption: ISelectOption | undefined = undefined;

        if (requireHostSelection && invitation && hostOptions.length && !hasInitialisedInvitationHost.current) {
          hostOption = hostOptions.find((option) => option.value === invitation.host) || hostOptions[0];
        } else if (requireHostSelection && !invitation && hostOptions.length) {
          hostOption = hostOptions[0];
        }

        form.onChange('visitorTypeOption', visitorTypeOption);
        form.onChange('hostOption', hostOption);

        hasInitialisedInvitationHost.current = true;
      }
    }, [isLoadingSiteRequirements, selectedSiteId, requireHostSelection]);

    /**
     * This effect is messy - but necessary given the dependant nature of the dropdown fields in this form.
     */
    useEffect(() => {
      const defaultZoneOption = zoneOptions.find((option) => option.value === defaultZone?.id);

      if (requireZoneSelection) {
        form.onChange(
          'zone',
          zoneOptions.find(({ value }) => value === invitation?.zoneId) || defaultZoneOption || zoneOptions[0],
        );
      }
    }, [defaultZone]);

    const formatNotesMessage = (limit: number, value: number) => {
      if (limit - value >= 0) {
        return `${formatSingularPlural(limit - value, 'character', 'characters')} left`;
      } else {
        return `${formatSingularPlural(
          value - limit,
          'character',
          'characters',
        )} over the limit. Please reduce your message`;
      }
    };

    const handleDurationChange = (newValues: DurationValues) => {
      newValues.expiry && form.onChange('expiry', newValues.expiry);
      newValues.startDate && form.onChange('validFrom', newValues.startDate);
      newValues.endDate && form.onChange('validTo', newValues.endDate);
    };

    return (
      <>
        <Form values={form.values} errors={form.errors} onFieldChange={form.onChange}>
          <Loader show={isLoadingSiteRequirements} fillAndCenter overlay />

          <InviteeList
            inviteeIds={inviteeIds}
            initialValues={initialValues.invitee}
            defaultCountryCode={siteOptions.find((option) => option.value === selectedSiteId)?.countryCode}
            onAddEmail={addEmail}
            onRemoveEmail={removeEmail}
            onDelete={removeInvitee}
            ref={inviteesRef}
          />

          <EmailNotificationControl
            canSendEmail={emailInvitations.size !== 0 && canSendEmail}
            emailCount={emailInvitations.size}
            totalCount={inviteeIds.length}
            onToggle={toggleSendEmail}
            disabled={emailInvitations.size === 0}
            isEditing={!!invitation}
            onAddInvitee={!invitation ? addInvitee : undefined}
          />

          <Grid container>
            <Grid md={6} alignItems='stretch'>
              <Input label='Company' name='company' data-st-ext='input-company' optional />
            </Grid>
            <Grid md={6} alignItems='stretch'>
              <Input label='Invite Subject' name='subject' data-st-ext='invite-subject' noSpaceReservation optional />
            </Grid>

            <Grid alignItems='stretch'>
              <TextArea
                name='adminNotes'
                label='Notes'
                rows={2}
                message={`${formatNotesMessage(
                  MAX_ADMIN_MESSAGE_CHAR_COUNT,
                  form.values.adminNotes?.length,
                )} (Only visible to admin)`}
                data-st-ext='admin-notes'
                noSpaceReservation
                optional
              />
            </Grid>

            <Grid md={requireHostSelection ? 4 : 6} alignItems='stretch'>
              <SingleSelect
                name='siteOption'
                label='Site'
                options={siteOptions}
                data-st-ext='select-site'
                noSpaceReservation
                onChange={(selection) => setSelectedSiteId(selection!.value as any as string)}
              />
            </Grid>
            <Grid md={requireHostSelection ? 4 : 6} alignItems='stretch'>
              <SingleSelect
                name='visitorTypeOption'
                label='Visitor Type'
                options={visitorTypeOptions}
                disabled={!visitorTypeOptions.length}
                data-st-ext='select-visitor-type'
                noSpaceReservation
                disableSearch
              />
            </Grid>
            {requireHostSelection && (
              <Grid md={4} alignItems='stretch'>
                <SingleSelect
                  name='hostOption'
                  label='Host'
                  options={hostOptions}
                  data-st-ext='select-host'
                  noSpaceReservation
                />
              </Grid>
            )}

            {requireZoneSelection && requireHostSelection && !!zoneOptions.length && (
              <Grid xs={4} alignItems='stretch'>
                <SingleSelect name='zone' label='Zone' options={zoneOptions} data-st-ext='select-zone' />
              </Grid>
            )}

            <Grid spacing={0} alignItems='stretch'>
              <InvitationDurationSelector
                onChange={handleDurationChange}
                invitation={invitation}
                errors={form.errors}
              />
            </Grid>

            <Grid alignItems='stretch'>
              <TextArea
                name='visitorNotes'
                label='Message to guest'
                rows={2}
                message={formatNotesMessage(MAX_VISITOR_MESSAGE_CHAR_COUNT, form.values.visitorNotes?.length)}
                data-st-ext='text-area-visitor-message'
                optional
              />
            </Grid>
          </Grid>
        </Form>

        {showConfirmationModal && (
          <ConfirmInvitationModal
            onClose={() => setShowConfirmationModal(false)}
            onCreateInvitations={async () =>
              createInvitations({ inviteesFormRef: inviteesRef, invitationDetails: form.values, canSendEmail })
            }
            numberOfGuestsWithoutEmail={inviteeIds.filter((id) => !emailInvitations.has(id)).length}
            loadingSubmission={false}
          />
        )}
      </>
    );
  },
);

CreateInvitationForm.displayName = 'CreateInvitationForm';
